import {
  ASSESSMENT_INSTRUMENT_IDS,
  CLIENT_TOOLS,
  HOMEWORK_ACTIVITY_MINUTES_MAX,
  HOMEWORK_ACTIVITY_TIMES_MAX,
  HOMEWORK_WORKOUT_TYPE_VALUES,
  LEARN_VIDEO_IDS,
  homeworkDefaultInstructionKey,
  homeworkDefaultTitleKey,
  homeworkTitleOf,
  homeworkTitleQuotesName,
  isSupportedWorksheetVideo,
  type HomeworkTitleTranslate,
  worksheetVideoSourceOf,
} from '@cc/schema';
import { format, parseISO } from 'date-fns';
import type { Locale } from 'date-fns';
import { colors } from '../../styles/colors';
import type {
  ClientHomework,
  ClientHomeworkCounts,
  ClientHomeworkPage,
  HomeworkComment,
  HomeworkSinceLastSession,
  PracticeHomeworkBoard,
} from '../../schema/be-graphql-generated';

/**
 * E34 · Homework on the portal (block 1b) — the pieces every homework screen shares: the fields each
 * read asks for, the vocabularies the Assign drawer offers, and small PURE helpers that turn a row
 * into what a column shows. No i18n here: helpers return KEYS, the screens translate.
 *
 * THE ASSIGN DRAWER'S KINDS: tool · assessment · worksheet (block 2a — the template library; the phone's
 * form is block 2b) · reading · task · link · video (block 6, the player; F-172 — the drawer and the
 * Builder offer it: a CopingCard Learn clip or a YouTube / Vimeo link). The model's last kind arrives
 * with its block — session (E46) — so the drawer does not offer what the phone cannot yet open.
 */

export const CLIENT_HOMEWORK_FIELDS = [
  'assignmentId',
  'subject',
  'requester',
  'requesterName',
  'practiceId',
  'kind',
  { target: ['type', 'ref', 'version', 'source'] },
  'title',
  'instructions',
  'dueAt',
  { schedule: ['rrule', 'dtstart', 'tzid', 'exdates'] },
  'reminderPolicy',
  'status',
  'overdue',
  'nextDueAt',
  'occurrenceAt',
  'completedAt',
  { evidence: ['focusType', 'focus'] },
  'note',
  'reasonCode',
  'reason',
  'performer',
  'awaitingReply',
  'clientCommentUnanswered', // Q-board-1 A: a cancelled row's latest comment is the client's — the grey marker
  'lastRemindedAt',
  'assignedAt',
  // Q-paper (1.5.0): may the client do it on paper (a tool: the therapist's switch; a worksheet: automatic) and the
  // photo a task or a worksheet was completed with
  'paperAllowed',
  { paperPhoto: ['fileRef', 'takenAt'] },
  // block 1e: each occurrence's outcome with its own record (Q-b1c-6 C, Q-b1e-3 A), and what the
  // latest notice went out on (Q-b1d-4 B: "none" = could not be reached)
  // Q-b1e-6 A: `repeats` — the second answers kept inside a done occurrence's window
  {
    outcomes: [
      'occurrenceAt',
      'outcome',
      'at',
      { evidence: ['focusType', 'focus'] },
      'repeats',
    ],
  },
  'lastNoticeChannels',
  'finishedAt', // Q-hw-page-3 A: the finished rows' order — the completing or skipping fact
];

/**
 * Q-hw-page-2 A (2026-10-04) — the Homework tab pages on the SERVER: 50 rows a page, the chips filter on the
 * server and count the whole client. `clientHomeworkPage` answers one page of rows, the pager's `total`, the
 * page SERVED and the chips' `counts`.
 */
export const HOMEWORK_PAGE_SIZE = 50;

export const CLIENT_HOMEWORK_PAGE_FIELDS = [
  { rows: CLIENT_HOMEWORK_FIELDS },
  'total',
  'page',
  'pageSize',
  {
    counts: [
      'all',
      'open',
      'overdue',
      'done',
      'skipped',
      'cancelled',
      'awaitingReply',
    ],
  },
];

export type HomeworkPageAnswer = Omit<ClientHomeworkPage, 'rows' | 'counts'> & {
  rows: ClientHomework[];
  counts: ClientHomeworkCounts;
};

export const SINCE_LAST_SESSION_FIELDS = [
  'subject',
  'sessionId',
  'assignmentId',
  'title',
  'kind',
  'dueAt',
  'status',
  'overdue',
  { evidence: ['focusType', 'focus'] },
  'reasonCode',
  'reason',
  'completedAt',
  'outcomeAt',
  'lastSessionStart', // Q-b1b-4 A: the heading's "Since <date>"
  'tzid', // D-1e-catchup-2-2026-09-29 (F-68): the schedule's zone — each occurrence is labelled in it
  // each occurrence answered since then, with its own record (Q-b1c-6 C, Q-b1e-3 A)
  // Q-b1e-6 A: `repeats` — the second answers kept inside a done occurrence's window
  {
    outcomes: [
      'occurrenceAt',
      'outcome',
      'at',
      { evidence: ['focusType', 'focus'] },
      'repeats',
    ],
  },
];

export const PRACTICE_BOARD_FIELDS = [
  'practiceId',
  'subject',
  'clientName',
  'clientEmail', // Q-board-2 A: shown nowhere, searched — resolved by the board query
  'therapistName',
  'openCount',
  'overdueCount',
  'doneThisWeek',
  'awaitingReply',
  'lastActivityAt',
  'enrollmentId',
  'pathwayId',
  'pathwayName',
  'pathwayPercentDone',
  'pendingDocuments',
  'assignmentIds',
  'homeworkAppReady', // the founder's Q-b1d-1′ A: the board marks a client whose app cannot show it yet
  'lng', // Q-cf-2: the client's language — the first selected client's picks the Assign drawer's Learn catalogue
];

export const COMMAND_PAYLOAD_FIELDS = ['assignmentId', 'recorded'];

/** The hub's comment thread (block 4) — what `homeworkComments` returns per comment. */
export const HOMEWORK_COMMENT_FIELDS = [
  'commentId',
  'assignmentId',
  'performer',
  'performerName',
  'fromClient',
  'mine',
  'text',
  'occurredAt',
];
export type HomeworkCommentRow = HomeworkComment;

export type HomeworkRow = ClientHomework;
export type SinceLastSessionRow = HomeworkSinceLastSession;
export type BoardRow = PracticeHomeworkBoard;

/** The bare id of a person reference: `users/45` and `45` both read "45". */
const bareId = (id?: string | null): string => String(id ?? '').replace(/^users\//, '');

/**
 * Q-board-2 A — does a board row match what the therapist typed? ANY PART of the first name, the last name,
 * "first last", the e-mail or the id (case-blind, extra spaces ignored). It runs on the DATA the page holds — the full
 * name the query returned — never on the rendered text, so it works while names are hidden ("45 · AK" on screen).
 * An empty search matches every row.
 */
export const boardSearchMatches = (
  row: Pick<BoardRow, 'subject' | 'clientName' | 'clientEmail'>,
  search: string,
): boolean => {
  const q = search.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!q) return true;
  const name = (row.clientName ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  return (
    name.includes(q) ||
    (row.clientEmail ?? '').toLowerCase().includes(q) ||
    bareId(row.subject).toLowerCase().includes(q) ||
    String(row.subject ?? '').toLowerCase().includes(q)
  );
};

/** The therapists of record the loaded rows name, once each, in alphabetical order (the admin's filter options). */
export const boardTherapistsOf = (rows: ReadonlyArray<Pick<BoardRow, 'therapistName'>>): string[] =>
  Array.from(new Set(rows.map((r) => (r.therapistName ?? '').trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  );

/**
 * The rows the board shows before its own tabs: #3 the HEADER "Filter by client" (a client chosen there leaves only
 * that client — `TherapistActivityReactContext.filterClientId`; empty = everyone) first, then the admin's therapist
 * filter (null / empty = all therapists), then the search. All three run over the loaded roster; sorting comes after.
 */
export const scopeBoardRows = <
  R extends Pick<BoardRow, 'subject' | 'clientName' | 'clientEmail' | 'therapistName'>,
>(
  rows: ReadonlyArray<R>,
  scope: { headerClientId?: string | null; therapist?: string | null; search?: string },
): R[] =>
  rows.filter(
    (r) =>
      (!scope.headerClientId || bareId(r.subject) === bareId(scope.headerClientId)) &&
      (!scope.therapist || (r.therapistName ?? '').trim() === scope.therapist) &&
      boardSearchMatches(r, scope.search ?? ''),
  );

/**
 * Q-board-2 A — the board's sortable columns: Open, Overdue, Done this week, Awaiting your reply, Last activity,
 * Pending documents. Ascending comparators for antd's `sorter`; the DEFAULT order stays the query's "overdue first"
 * (no column starts sorted). A client with no activity yet is the oldest.
 */
export const BOARD_SORTABLE = [
  'open',
  'overdue',
  'doneThisWeek',
  'awaitingReply',
  'lastActivity',
  'pendingDocuments',
] as const;
export type BoardSortKey = (typeof BOARD_SORTABLE)[number];

const instantOf = (iso?: string | null): number => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
};

export const BOARD_COMPARATORS: Record<
  BoardSortKey,
  (a: BoardRow, b: BoardRow) => number
> = {
  open: (a, b) => a.openCount - b.openCount,
  overdue: (a, b) => a.overdueCount - b.overdueCount,
  doneThisWeek: (a, b) => a.doneThisWeek - b.doneThisWeek,
  awaitingReply: (a, b) => a.awaitingReply - b.awaitingReply,
  lastActivity: (a, b) => {
    const x = instantOf(a.lastActivityAt);
    const y = instantOf(b.lastActivityAt);
    return x === y ? 0 : x < y ? -1 : 1;
  },
  pendingDocuments: (a, b) => a.pendingDocuments - b.pendingDocuments,
};

/** The kinds the drawer offers (see the note above) — block 2a adds `worksheet` (the template library). */
export const ASSIGNABLE_KINDS = [
  'tool',
  'assessment',
  'worksheet',
  'reading',
  'task',
  'link',
  'video',
] as const;
export type AssignableKind = (typeof ASSIGNABLE_KINDS)[number];

/**
 * PORTAL-SECTIONS-2026-10-22 §1.8 · what a tool's own Assign button opens the drawer with. Every field is optional: a caller
 * that passes only `{ kind, target?, title? }` (the Assessments tab, the sessions panel) gets the drawer exactly as before.
 */
export type AssignDefaults = {
  /** A fixed title, or one that follows the chosen ref (FA: "Record each time it happens - ...: {name}"). */
  title?: string | ((refLabel: string | null) => string);
  reminderPolicy?: 'default' | 'dueOnly' | 'none';
  deadline?:
    | {
        shape: 'due';
        /** The picker may stay empty: dueAt null, schedule null. */
        optional?: boolean;
        /** The hour the picker's time takes once a day is picked (default 18:00, Q-due-1 A); MI passes 9. */
        defaultHour?: number;
      }
    | {
        shape: 'repeat';
        frequency: 'daily' | 'weekly';
        hour: number;
        minute: number;
        startInDays: number;
        count: number;
        countMax: number;
        countUnit: 'days' | 'weeks';
      };
  ref?: {
    label: string;
    options: Array<{ value: string; label: string }>;
    value?: string;
    /** MI's "Any change": choosing it sends NO ref. */
    any?: { value: string; label: string };
    requiredMessage?: string;
  };
};

export type AssignPreset = {
  kind: AssignableKind;
  target?: string;
  title?: string;
  /** The tool's own targets, in order: while kind is tool the target list offers only these, the Kind row is not shown. */
  targets?: readonly string[];
  /** Per target: applied on open and whenever the target changes to it. */
  defaults?: Partial<Record<string, AssignDefaults>>;
};

/** The one title a defaults entry gives for a ref label (null: no ref chosen / a fixed title). */
export const defaultTitleOf = (d: AssignDefaults | undefined, refLabel: string | null): string | null => {
  if (!d || d.title === undefined) return null;
  return typeof d.title === 'function' ? d.title(refLabel) : d.title;
};

/**
 * Q-ru-3 A · the Title field's placeholder while it is empty: THE PICKED TOOL'S default title in the portal's language (the one
 * shared table `@cc/schema` `homeworkTitles` the API renders from), so the therapist sees what the client will get. Null = no
 * default to show (no tool picked, a kind without one, or a default that quotes a behavior not yet chosen) - the caller keeps the
 * generic example. The field's VALUE stays empty: an empty title is sent as `title: null`.
 */
export const defaultTitlePlaceholder = (
  kind: AssignableKind,
  targetType: string | null | undefined,
  name: string | null | undefined,
  t: HomeworkTitleTranslate,
): string | null => {
  if (!targetType) return null;
  const target = { type: targetType };
  if (homeworkDefaultTitleKey(kind, target) === null) return null;
  if (homeworkTitleQuotesName(kind, target) && !name) return null;
  const text = homeworkTitleOf({ kind, target, titleName: name ?? null }, t);
  return text.trim() ? text : null;
};

/**
 * "Default instructions: A" (2026-10-25) - the Instructions field's placeholder while it is empty: THE PICKED TOOL'S default
 * instructions in the portal's language (the one shared table `@cc/schema` `homeworkInstructions` the API renders from). Null =
 * the picked target has none. The VALUE stays empty: empty instructions are sent as `null` (= the default).
 */
export const defaultInstructionsPlaceholder = (
  kind: AssignableKind,
  targetType: string | null | undefined,
  t: HomeworkTitleTranslate,
): string | null => {
  const key = targetType ? homeworkDefaultInstructionKey(kind, { type: targetType }) : null;
  return key ? t(key) : null;
};

/**
 * The Toolkit tools a practice can assign, as `target.type` — the vocabulary the evidence matcher
 * (block 1d) maps to each tool's own completion fact: thoughtTest ← the thought test's outcome,
 * abcModel ← AbcModelCreated, downwardArrow ← DownwardArrowCreated, worryTree ← WorryTreeCreated,
 * viciousCycle ← ViciousCycleCreated, behaviorExperiment ← ExperimentCompleted, exposure ←
 * ExpositionFinished, meditation ← MeditationSessionFinished, checkIn ← CheckInRecorded, diaryCard ← DiaryCardFilled (cc-homework, "Homework — Completion
 * evidence & notifications", the HomeworkAwaitingEvidence todo's fact list).
 */
export const TOOL_TARGETS = [
  'thoughtTest',
  'abcModel',
  'downwardArrow',
  'worryTree',
  'viciousCycle',
  'behaviorExperiment',
  'exposure',
  'meditation',
  // E37 — the daily check-in and the diary card: the client's own tools, no record to name (the matcher
  // completes the homework on the CLIENT'S OWN entry; a therapist's entry in session completes nothing)
  'checkIn',
  'diaryCard',
  // E47 — the daily mode check-in (completes on the client's own ModeCheckInRecorded; no record to name)
  'modeCheckIn',
  // E36 — the Values tools (each completes on ITS fact: ValuesSorted · ValuesWheelRated, the client's own · ValuedLivingRated)
  'valuesSort',
  'valuesWheel',
  'valuedLivingQuestionnaire',
  // E53 — the wind rose (completes on the client's own choice)
  'windRose',
  // E41 — the Behavioral activation steps (each completes on ITS fact, the client's own: an hour logged · a ranking · a plan · a contract)
  'activityMonitoring',
  'activityRanking',
  'activityPlanning',
  'supportContract',
  // E45 — the sleep diary (each night's confirmation, the client's own, completes that day's occurrence of a daily step)
  'sleepDiary',
  // E48 — functional analysis: a record of the target behaviour (target.ref = the target; each week completes on the client's own first record)
  'chainAnalysis',
  // E49 — Motivation: the rulers, the pros and cons, the change plan (each completes on ITS fact, the client's own: ChangeRated · DecisionalBalanceWritten · ChangePlanWritten; ref = the change)
  'changeRating',
  'decisionalBalance',
  'changePlan',
] as const;

/** The instruments a practice can assign (the app's PHQ-9 and GAD-7; E35: DASS-21 · PCL-5 · GDS-15). */
export const ASSESSMENT_TARGETS: readonly string[] = ASSESSMENT_INSTRUMENT_IDS; // N-5: the shared catalogue, so a new instrument cannot be missed

/**
 * N-11 · the tool targets grouped by the client TOOL that owns them (`CLIENT_TOOLS.homeworkTargets`), in the tool catalogue's
 * canonical order and each tool's own step order - the Assign drawer lists them under the tool's standard name. A target no
 * tool owns (none today) is kept, in a group of its own with `tool: null`, so nothing disappears from the list.
 */
export const toolTargetGroups = (targets: readonly string[]): Array<{ tool: string | null; targets: string[] }> => {
  const wanted = new Set(targets);
  const seen = new Set<string>();
  const out: Array<{ tool: string | null; targets: string[] }> = [];
  for (const def of CLIENT_TOOLS) {
    const own = def.homeworkTargets.filter((x) => wanted.has(x) && !seen.has(x));
    if (!own.length) continue;
    own.forEach((x) => seen.add(x));
    out.push({ tool: def.key, targets: own });
  }
  const rest = targets.filter((x) => !seen.has(x));
  if (rest.length) out.push({ tool: null, targets: rest });
  return out;
};

export const REMINDER_POLICIES = ['default', 'dueOnly', 'none'] as const;

/** `{type}` of the target each kind points at (a task has none). */
export const targetTypeOf = (
  kind: AssignableKind,
  toolOrRef: string | undefined,
): string | null => {
  switch (kind) {
    case 'tool':
      return toolOrRef ?? null;
    case 'assessment':
      return 'assessment';
    case 'reading':
      return 'article';
    case 'worksheet':
      // `{type:"worksheet", ref:<templateId>}` — the server pins the template's latest version
      return 'worksheet';
    case 'link':
      return 'url';
    case 'video':
      return 'video';
    default:
      return null;
  }
};

/**
 * F-173 — a day that ENDS a sentence ("Homework since 29 September."). The short month of some locales
 * carries its own full stop (Russian: "29 сент."), and the sentence's full stop then doubled («С 29 сент..»).
 * The full month name has none in any locale, so a date that closes a sentence is always written with it.
 */
export const sentenceDayOf = (iso: string, locale: Locale): string =>
  format(parseISO(iso), 'd MMMM', { locale });

/**
 * F-174 — the reason a cancelled step carries when its PATHWAY was withdrawn. The domain writes it as the
 * fact (`withdrawnStepReason`: `pathway withdrawn` or `pathway withdrawn: <the practice's reason>` — the
 * board's example, kept), in English. The reader sees it in their own language: this recognises the prefix
 * and hands back the practice's own words (never translated), and the screen picks the key.
 */
export const withdrawnReasonOf = (
  reason: string | null | undefined,
): { withdrawn: false } | { withdrawn: true; reason: string | null } => {
  const r = (reason ?? '').trim();
  if (r === 'pathway withdrawn') return { withdrawn: true, reason: null };
  const m = /^pathway withdrawn:\s*([\s\S]*)$/.exec(r);
  return m ? { withdrawn: true, reason: m[1].trim() || null } : { withdrawn: false };
};

/** The i18n key + params of a cancelled row's reason line, in the reader's language. */
export const cancelReasonLine = (
  reason: string,
): { key: string; params?: { reason: string } } | null => {
  const w = withdrawnReasonOf(reason);
  if (!w.withdrawn) return null;
  return w.reason
    ? { key: 'homework.hub.cancelWithdrawnWith', params: { reason: w.reason } }
    : { key: 'homework.hub.cancelWithdrawn' };
};

/** A video step's target as the wire and the phone read it (`homeworkVideoOf` on the phone). */
export type VideoTarget = {
  type: 'video';
  source: 'learn' | 'youtube' | 'vimeo';
  ref: string;
};

/**
 * F-172 — the video target of ONE string, the way the Assign drawer's Target and the Builder's step hold it:
 * a CopingCard Learn clip's id (E15's catalogue — either language), or an https YouTube / Vimeo link — the
 * links the phone's player can play (`playerOf`: a video id in the address; an unlisted YouTube or a private
 * Vimeo link with its hash plays). The source is read off the string, never chosen twice. Null when the string
 * is neither, so the drawer says what is missing instead of assigning a step the phone cannot open.
 */
export const videoTargetOf = (
  value: string | null | undefined,
): VideoTarget | null => {
  const ref = (value ?? '').trim();
  if (!ref) return null;
  if (LEARN_VIDEO_IDS.includes(ref)) return { type: 'video', source: 'learn', ref };
  const source = worksheetVideoSourceOf(ref);
  if (!source || !isSupportedWorksheetVideo(source, ref)) return null;
  // the phone plays a link that names a video (an id in the address) — a channel or home page is not one
  const named =
    source === 'youtube'
      ? /(?:youtu\.be\/|[?&]v=|\/embed\/|\/shorts\/)[A-Za-z0-9_-]{6,20}/
      : /vimeo\.com\/(?:video\/)?\d+/;
  return named.test(ref) ? { type: 'video', source, ref } : null;
};

export type StatusChip = {
  key: string;
  color: string;
};

/** The chip a row's status reads as (overdue wins over open/viewed — "overdue = due passed, not done"). */
export const statusChipOf = (row: {
  status: string;
  overdue?: boolean | null;
}): StatusChip => {
  if ((row.status === 'open' || row.status === 'viewed') && row.overdue) {
    return { key: 'overdue', color: 'red' };
  }
  switch (row.status) {
    case 'open':
      return { key: 'open', color: 'blue' };
    case 'viewed':
      return { key: 'viewed', color: 'geekblue' };
    case 'done':
      return { key: 'done', color: 'green' };
    case 'skipped':
      return { key: 'skipped', color: 'orange' };
    case 'cancelled':
      return { key: 'cancelled', color: 'default' };
    default:
      return { key: row.status, color: 'default' };
  }
};

export type HubFilter =
  | 'all'
  | 'open'
  | 'overdue'
  | 'done'
  | 'skipped'
  | 'cancelled'
  | 'awaitingReply';

export const HUB_FILTERS: HubFilter[] = [
  'all',
  'open',
  'overdue',
  'done',
  'skipped',
  'cancelled',
  'awaitingReply',
];

/**
 * Q-badge-1 A (2026-10-03) — the "Awaiting your reply" badge is soft AMBER: the muted sage pill was about 3:1
 * contrast and failed WCAG AA. Background `--adjacent-2` #FFCA98, text and icon `--adjacent-3` #623F18, border
 * `--adjacent-1` #F0BD8B (about 6.5:1) — from `styles/colors.ts`, never a hex literal in a component. The hub and the
 * board wear the same one.
 */
export const AWAITING_BADGE_STYLE = {
  background: colors['--adjacent-2'],
  color: colors['--adjacent-3'],
  borderColor: colors['--adjacent-1'],
} as const;

/**
 * Q-board-1 A — what a hub row says about the client's unanswered comment: the amber badge on a live row whose latest
 * comment is the client's; on a CANCELLED row (which never counts) a muted grey "No longer relevant" marker, never the
 * amber badge; nothing otherwise. A cancelled row's `awaitingReply` is never read — the server drops it from the
 * count, and `clientCommentUnanswered` says the comment is still the client's.
 */
export const awaitingMarkerOf = (row: {
  status: string;
  awaitingReply?: boolean | null;
  clientCommentUnanswered?: boolean | null;
}): 'awaiting' | 'noLongerRelevant' | null => {
  if (row.status === 'cancelled')
    return row.clientCommentUnanswered ? 'noLongerRelevant' : null;
  return row.awaitingReply ? 'awaiting' : null;
};

/** Does a row fall under a filter chip? (the chips count by status; overdue = open or viewed and past due) */
export const matchesFilter = (row: HomeworkRow, f: HubFilter): boolean => {
  const open = row.status === 'open' || row.status === 'viewed';
  switch (f) {
    case 'all':
      return true;
    case 'open':
      return open;
    case 'overdue':
      return open && !!row.overdue;
    case 'awaitingReply':
      // Q-board-1 A: the hub's count is the board's number — a cancelled row never counts
      return awaitingMarkerOf(row) === 'awaiting';
    default:
      return row.status === f;
  }
};

/** A self-set row is the client's own: visible, never editable from the portal. */
export const isSelfSet = (row: { practiceId?: string | null }): boolean =>
  !row.practiceId;

/**
 * "Could not be reached" (Q-b1d-4 B): the row's latest notice went out on no channel — no push token
 * and no e-mail. Null (no notice yet) and any real channel say nothing.
 */
export const couldNotBeReached = (row: {
  lastNoticeChannels?: string | null;
}): boolean => row.lastNoticeChannels === 'none';

export type OccurrenceEntry = {
  occurrenceAt: string | null;
  outcome: 'done' | 'skipped' | 'missed' | 'open';
  at?: string | null;
  evidence?: { focusType: string; focus: unknown } | null;
  /** A done occurrence's second answers inside its window (Q-b1e-6 A) — "done · repeated 1". */
  repeats?: number;
};

type OutcomeRow = {
  schedule?: { tzid?: string | null } | null;
  status: string;
  nextDueAt?: string | null;
  outcomes?: ReadonlyArray<{
    occurrenceAt?: string | null;
    outcome: string;
    at?: string | null;
    evidence?: { focusType: string; focus: unknown } | null;
    repeats?: number | null;
  }> | null;
};

/**
 * A recurring row's occurrences in order, the open one last — the hub's "Oct: done · Nov: open"
 * (the founder's Q-b1c-6 C): each counted occurrence and each missed one (the server's `outcomes`),
 * then the occurrence still open (`nextDueAt` — the open occurrence — while the row is not withdrawn).
 * A one-off row has its one outcome (or none) and no open entry.
 */
export const occurrenceEntries = (row: OutcomeRow): OccurrenceEntry[] => {
  const entries: OccurrenceEntry[] = (row.outcomes ?? []).map((o) => ({
    occurrenceAt: o.occurrenceAt ?? null,
    outcome: o.outcome as OccurrenceEntry['outcome'],
    at: o.at ?? null,
    evidence: o.evidence ?? null,
    // a done occurrence answered again inside its window (Q-b1e-6 A) — shown only when there were any
    ...(o.outcome === 'done' && o.repeats ? { repeats: o.repeats } : {}),
  }));
  if (
    row.schedule &&
    row.nextDueAt &&
    (row.status === 'open' || row.status === 'viewed')
  ) {
    entries.push({ occurrenceAt: row.nextDueAt, outcome: 'open' });
  }
  return entries;
};

/**
 * How an occurrence is named in its row: the MONTH for a monthly rule ("Oct"), the day otherwise
 * ("5 Oct") — in the zone the rule runs in, so a 9 pm Monday is never named for Tuesday.
 */
export const occurrenceLabel = (
  iso: string,
  rrule: string | null | undefined,
  tzid: string | null | undefined,
  locale: string,
): string => {
  const monthly = /FREQ=MONTHLY/.test(rrule ?? '');
  try {
    return new Intl.DateTimeFormat(locale, {
      ...(monthly ? {} : { day: 'numeric' }),
      month: 'short',
      ...(tzid ? { timeZone: tzid } : {}),
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
};

export const isOpenRow = (row: { status: string }): boolean =>
  row.status === 'open' || row.status === 'viewed';

/**
 * Q-mt-2 A — a practice act the row can no longer take, known when the dialog opens (never from a
 * server refusal): the reason's i18n key suffix (`homework.lock.<suffix>`), or null when the act is open
 * to the therapist. Mirrors the hub's own rule for which actions it offers: a live row is one that is not
 * cancelled and is either open/viewed or recurring.
 */
export const actLockOf = (
  act: 'edit' | 'cancel' | 'reopen' | 'remind',
  row: { status: string; schedule?: unknown | null },
  notConnected = false,
): 'notConnected' | 'cancelled' | 'done' | 'skipped' | 'open' | null => {
  // the reminder is not one of the three E34 error-display dialogs
  if (act === 'remind') return null;
  // a client who shares nothing any more keeps the practice's WITHDRAWAL (Q-b1b-1 A: Cancel asks the roster, not the
  // fourth question) — only Edit and Reopen reach the client again
  if (notConnected && act !== 'cancel') return 'notConnected';
  switch (act) {
    case 'edit':
      if (row.status === 'cancelled') return 'cancelled';
      if (isOpenRow(row) || row.schedule) return null;
      return row.status === 'done' || row.status === 'skipped'
        ? row.status
        : null;
    case 'cancel':
      return row.status === 'cancelled' ? 'cancelled' : null;
    case 'reopen':
      return isOpenRow(row) ? 'open' : null;
  }
};

/**
 * F-220 — why a paper photo that was minted a link for failed to LOAD. The link is fresh when the viewer
 * opens, so an image that fails BEFORE `expiresAt` is the file's fault (an iPhone HEIC a browser cannot
 * show), not an expired link: say so and offer the file — never a pointless "Try again". At or after
 * `expiresAt` (or with no readable expiry) it is the expired-link case, with a fresh link on offer.
 */
export const photoFailureOf = (
  expiresAt: string | null | undefined,
  now: Date = new Date(),
): 'cannotDisplay' | 'expired' => {
  const expires = expiresAt ? new Date(expiresAt).getTime() : NaN;
  return Number.isFinite(expires) && now.getTime() < expires
    ? 'cannotDisplay'
    : 'expired';
};

/** "at most one manual reminder per assignment per day (the button greys out)" — the viewer's day. */
export const remindedToday = (
  lastRemindedAt: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!lastRemindedAt) return false;
  const at = new Date(lastRemindedAt);
  return (
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  );
};

/**
 * Q-due-1 A (1.5.0) — the one-off "Due at" picker pre-selects 18:00 when the therapist picks a DATE (antd
 * filled 00:00 because the picker had no default time). The repeat editor and the phone's Add-my-own already
 * default to 18:00. The picker's `showTime.defaultValue` takes a Date whose time-of-day is used; changeable.
 */
export const DEFAULT_DUE_HOUR = 18;
export const defaultDueTime = (): Date => {
  const d = new Date(2000, 0, 1, DEFAULT_DUE_HOUR, 0, 0, 0);
  return d;
};

/** The browser's IANA zone (what the therapist's own "today" and "due at" mean). */
export const browserZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/**
 * The Learn language of a client's `lng` (Q-fix-1 C, 2026-09-30) — the phone's own normalisation
 * (`startsWith('ru')` → ru, any other value → en). Missing / empty → null: the caller falls back to the
 * therapist's portal language (Q-cf-2).
 */
export const learnLanguageOf = (lng: string | null | undefined): 'en' | 'ru' | null => {
  const value = lng?.trim().toLowerCase();
  if (!value) return null;
  return value.startsWith('ru') ? 'ru' : 'en';
};

/**
 * The ONE Learn catalogue a video target offers (Q-cf-2, the founder 2026-09-30: "by default only one
 * catalog, selected by the client's first language"; every catalogue at once is never a default):
 * the client's language — with several clients selected (the practice board) the FIRST one's — and, when
 * that is unknown or there is no client (the pathway Builder), the therapist's portal UI language.
 * Choosing another catalogue is a later round ("later should have a way to select/customize").
 */
export const learnLanguageFor = (
  firstClientLng: string | null | undefined,
  uiLocale: string | null | undefined,
): 'en' | 'ru' => learnLanguageOf(firstClientLng) ?? learnLanguageOf(uiLocale) ?? 'en';

/**
 * Q-mt-2 A — is the client NOT CONNECTED for acting? True when the roster read was refused, or the
 * access read says the client shares nothing with the caller (`canAssign === false`). Unknown (the
 * answer has not arrived, or failed) blocks nothing: the server still refuses every write, and a
 * flash of a false alert would be worse than a drawer that fills in a moment later.
 */
export const isNotConnected = (
  refused: boolean,
  canAssign: boolean | null | undefined,
): boolean => refused || canAssign === false;

/**
 * E43 · a task's ACTIVITY TARGET in the Assign drawer (Q-e43g1-8 A, 2026-10-09). The drawer holds a draft of
 * strings the therapist types; `activityTargetOf` turns it into the board's shape —
 * `{kind:"workout", workoutType:"walking", minutesEach:20, times:3, per:"week"}` — or null when the section is
 * off (the section is optional and sends null when cleared). The workout types are the validator's (the ones
 * the phone reports and the evidence matcher compares); `ANY_WORKOUT` sends `workoutType: null`.
 */
export const WORKOUT_TYPES = HOMEWORK_WORKOUT_TYPE_VALUES;
export const ANY_WORKOUT = 'any';
export { HOMEWORK_ACTIVITY_MINUTES_MAX, HOMEWORK_ACTIVITY_TIMES_MAX };

export type ActivityTargetDraft = {
  on: boolean;
  /** A workout type, or `ANY_WORKOUT`. */
  workoutType: string;
  minutesEach: string;
  times: string;
};

export const freshActivityTarget = (): ActivityTargetDraft => ({
  on: false,
  workoutType: 'walking',
  minutesEach: '20',
  times: '3',
});

const wholeIn = (raw: string, max: number): number | null => {
  const text = raw.trim();
  if (!/^\d{1,4}$/.test(text)) return null;
  const n = Number(text);
  return n >= 1 && n <= max ? n : null;
};

/** The draft's problem as an i18n key (the section is on and a number is missing or out of range), else null. */
export const activityTargetProblem = (d: ActivityTargetDraft): string | null => {
  if (!d.on) return null;
  if (wholeIn(d.minutesEach, HOMEWORK_ACTIVITY_MINUTES_MAX) === null)
    return 'homework.assign.required.activityMinutes';
  if (wholeIn(d.times, HOMEWORK_ACTIVITY_TIMES_MAX) === null)
    return 'homework.assign.required.activityTimes';
  return null;
};

/** The command's `activityTarget`: the board's shape, or null when the section is off (or not valid). */
export const activityTargetOf = (
  d: ActivityTargetDraft,
): Record<string, unknown> | null => {
  if (!d.on || activityTargetProblem(d)) return null;
  return {
    kind: 'workout',
    workoutType: d.workoutType === ANY_WORKOUT ? null : d.workoutType,
    minutesEach: wholeIn(d.minutesEach, HOMEWORK_ACTIVITY_MINUTES_MAX),
    times: wholeIn(d.times, HOMEWORK_ACTIVITY_TIMES_MAX),
    per: 'week',
  };
};
